import React, { useState, useContext } from 'react';
import { motion } from 'framer-motion';
import { dismissModal } from '../../../service/modal-service';
import { UserContext } from '../../../context/UserContext';
import { SystemError } from '../../error/SystemError';
import { uploadImage } from '../../../api/user/post-api';
import { photoUpload } from '../../../js/photo-upload';
import BottomSheet from '../../commons/BottomSheet';
import { X, FolderPlus, ImagePlus, Globe, Users, Lock, Pencil } from 'lucide-react';
import ImageEditor from '../../commons/media-editor/ImageEditor';

export const NewAlbum = (props) => {
    const getAlbums = props.getAlbums
    
    const [isOpen, setIsOpen] = useState(true)
    const [formValues, setFormValues] = useState({
        albumName: "",
        albumPrivacy: "Public",
    })

    //File Attachment
    const [filesToAttach, setFilesToAttach] = useState([])
    const [filesToUpload, setFilesToUpload] = useState([])
    const [uploadPercent, setUploadPercent] = useState(0)

    const [isUploading, setIsUploading] = useState(false)
    const [isSystemError, setIsSystemError] = useState(false)
    const [errorContent, setErrorContent] = useState({'header': "", 'body': ""})

    // Image editor state
    const [isEditorOpen, setIsEditorOpen] = useState(false)
    const [editorImageSrc, setEditorImageSrc] = useState(null)
    const [editingIndex, setEditingIndex] = useState(null)
    
    const { userId, userFullName } = useContext(UserContext)

    const handleClose = () => {
        dismissModal()
    }

    const { removeFile, handleFileAttachment } = photoUpload(setFilesToAttach, filesToUpload, setErrorContent, setIsSystemError);

    const privacyOptions = [
        { value: 'Public', label: 'Everyone', icon: Globe },
        { value: 'Strings', label: 'Connections', icon: Users },
        { value: 'Private', label: 'Only Me', icon: Lock },
    ]

    const handleChange = (e) => {   
        const { name, value } = e.target;
        setFormValues({...formValues, [name]: value})
    }

    const uploadPhotosToAlbum = async (x) => {
        try {
            await uploadImage(filesToUpload[x].image, userId, userFullName, "", formValues.albumName, formValues.albumPrivacy, "public", "");
            setUploadPercent(Math.round(((x + 1) / (filesToUpload.length)) * 100));
            if ((filesToUpload.length - 1) === x) {
                setIsUploading(false);
                setFilesToUpload([]);
                getAlbums();
                dismissModal();
            } else {
                x++;
                uploadPhotosToAlbum(x);
            }
        } catch (error) {
            console.log(error);
            setErrorContent({ 'header': "Error uploading album", 'body': "Please try again. If problem persists, contact us at support@stringssg.com" });
            setIsSystemError(true);
            setIsUploading(false);
        }
    };

    const openPhotoEditor = (index) => {
        setEditingIndex(index)
        setEditorImageSrc(filesToAttach[index].image)
        setIsEditorOpen(true)
    }

    const handlePhotoEdited = ({ dataURL, file }) => {
        setFilesToAttach(prev => { const u = [...prev]; u[editingIndex] = { ...u[editingIndex], image: dataURL }; return u })
        setFilesToUpload(prev => { const u = [...prev]; u[editingIndex] = { ...u[editingIndex], image: file }; return u })
        setIsEditorOpen(false); setEditorImageSrc(null); setEditingIndex(null)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        setIsUploading(true)
        if (!formValues.albumName.trim()) {
            setErrorContent({ 'header': "Album Name Required", 'body': "Please enter a name for your album before creating it." });
            setIsSystemError(true);
            setIsUploading(false);
            return
        }
        if(filesToAttach.length === 0){
            setErrorContent({'header': "No Photos Selected", 'body': "Please select at least one photo to create the album"});
            setIsSystemError(true);
            setIsUploading(false);
            return
        }
        setFilesToAttach([])
        if(filesToUpload.length > 0){
            uploadPhotosToAlbum(0)
        }
    }

    return (
        <>
            <BottomSheet isOpen={isOpen} onClose={handleClose} maxWidth="lg">
                <BottomSheet.Header>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                            <FolderPlus className="w-5 h-5 text-orange-600" />
                        </div>
                        <BottomSheet.Title>New Album</BottomSheet.Title>
                    </div>
                </BottomSheet.Header>

                <BottomSheet.Content>
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-5">
                            {/* Album Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Album Name
                                </label>
                                <input
                                    type="text"
                                    className="w-full px-4 py-3 text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                                    placeholder="Enter album name"
                                    name="albumName"
                                    value={formValues.albumName}
                                    onChange={(e) => handleChange(e)}
                                    required
                                />
                            </div>
                            
                            {/* Privacy Options */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Who can see this album?
                                </label>
                                <div className="flex gap-2">
                                    {privacyOptions.map((option) => {
                                        const Icon = option.icon
                                        const isSelected = formValues.albumPrivacy === option.value
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setFormValues({...formValues, albumPrivacy: option.value})}
                                                className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                                                    isSelected 
                                                        ? 'border-orange-500 bg-orange-50' 
                                                        : 'border-gray-100 hover:border-gray-200'
                                                }`}
                                            >
                                                <Icon className={`w-4 h-4 ${isSelected ? 'text-orange-500' : 'text-gray-400'}`} />
                                                <span className={`text-xs font-medium ${isSelected ? 'text-orange-600' : 'text-gray-600'}`}>
                                                    {option.label}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                            
                            {/* Photo Upload Area */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Photos
                                </label>
                                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-4">
                                    {filesToAttach.length > 0 ? (
                                        <div className="space-y-4">
                                            <div className="grid grid-cols-3 gap-2">
                                                {[...filesToAttach].map((file, i) => 
                                                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden group">
                                                        <div 
                                                            className="h-full w-full bg-cover bg-center"
                                                            style={{backgroundImage: `url(${file.image})`}}
                                                        />
                                                        <button type="button" onClick={() => openPhotoEditor(i)}
                                                            className="absolute bottom-1 left-1 w-6 h-6 rounded-full bg-orange-500/90 text-white flex items-center justify-center" title="Edit photo">
                                                            <Pencil className="w-3 h-3" />
                                                        </button>
                                                        <button 
                                                            type="button" 
                                                            onClick={() => removeFile(i)}
                                                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                            <label className="flex items-center justify-center gap-2 py-2 text-orange-600 cursor-pointer hover:text-orange-700">
                                                <ImagePlus className="w-4 h-4" />
                                                <span className="text-sm font-medium">Add more photos</span>
                                                <input 
                                                    className="hidden"
                                                    type="file" 
                                                    name="photo"
                                                    accept="image/*"
                                                    capture={false}
                                                    onChange={(e) => handleFileAttachment(e)}
                                                    multiple
                                                />
                                            </label>
                                        </div>
                                    ) : (
                                        <label className="flex flex-col items-center justify-center py-8 cursor-pointer">
                                            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-3">
                                                <ImagePlus className="w-7 h-7 text-orange-500" />
                                            </div>
                                            <p className="text-sm font-medium text-gray-900">Add photos to your album</p>
                                            <p className="text-xs text-gray-500 mt-1">PNG, JPG, or GIF</p>
                                            <input 
                                                className="hidden"
                                                type="file" 
                                                name="photo"
                                                accept="image/*"
                                                capture={false}
                                                onChange={(e) => handleFileAttachment(e)}
                                                multiple
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                            
                            {/* Upload Progress */}
                            {isUploading && (
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">Uploading...</span>
                                        <span className="text-orange-600 font-medium">{uploadPercent}%</span>
                                    </div>
                                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <motion.div 
                                            className="h-full bg-linear-to-r from-orange-500 to-amber-500 rounded-full"
                                            initial={{ width: 0 }}
                                            animate={{ width: `${uploadPercent}%` }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </form>
                </BottomSheet.Content>

                <BottomSheet.Footer>
                    <div className="flex gap-3">
                        <BottomSheet.CloseButton>Cancel</BottomSheet.CloseButton>
                        <motion.button 
                            whileTap={{ scale: 0.98 }}
                            type="button"
                            onClick={handleSubmit}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold text-white bg-linear-to-r from-orange-500 to-amber-500 shadow-lg shadow-orange-200 disabled:opacity-50" 
                            disabled={isUploading}
                        >
                            {isUploading ? 'Creating Album...' : 'Create Album'}
                        </motion.button>
                    </div>
                </BottomSheet.Footer>
            </BottomSheet>

            <ImageEditor
                isOpen={isEditorOpen}
                onClose={() => { setIsEditorOpen(false); setEditorImageSrc(null); setEditingIndex(null) }}
                onSave={handlePhotoEdited}
                imageSrc={editorImageSrc}
            />

            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError} />}
        </>
    )
}
